<template>
  <v-dialog v-model="visible" scrollable persistent max-width="800" @keydown.esc="cancelForm()">
    <v-card class="equipamiento-form-card">
      <v-card-title class="primary white--text py-4">
        <v-icon left color="white">{{ isNewRecord ? 'mdi-tools' : 'mdi-pencil' }}</v-icon>
        {{ title }}
        <v-spacer></v-spacer>
        <v-btn icon dark @click="cancelForm()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-4">
        <v-form ref="equipamientoForm" v-model="validations.isValid">
          <v-container fluid>
            <!-- Información básica del equipamiento -->
            <v-row>
              <v-col cols="12">
                <div class="section-title">
                  <v-icon color="primary" left>mdi-tools</v-icon>
                  <span>Información del Equipamiento</span>
                </div>
              </v-col>
              <v-col cols="12">
                <v-text-field 
                  v-model="fields.name" 
                  :rules="validations.name" 
                  :error-messages="errors.name"
                  :disabled="loading" 
                  label="Nombre del Equipamiento*" 
                  prepend-inner-icon="mdi-tools" 
                  required 
                  outlined
                  dense
                  hint="Ej: Kit de Herramientas, Linterna LED, Cable de Carga"
                  persistent-hint
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Asignación de tipos de vehículos -->
            <v-row class="mt-2">
              <v-col cols="12">
                <div class="section-title">
                  <v-icon color="primary" left>mdi-car-multiple</v-icon>
                  <span>Tipos de Vehículos Asignados</span>
                </div>
              </v-col>
              <v-col cols="12">
                <v-card outlined class="pa-3">
                  <div class="text-subtitle-2 mb-3 grey--text text--darken-1">
                    Selecciona los tipos de vehículos que requieren este equipamiento:
                  </div>
                  <v-row>
                    <v-col 
                      cols="12" 
                      md="4" 
                      v-for="vehicleType in availableVehicleTypes" 
                      :key="vehicleType.value"
                    >
                      <v-checkbox
                        v-model="fields.vehicle_types"
                        :value="vehicleType.value"
                        :label="vehicleType.label"
                        :color="vehicleType.color"
                        :disabled="loading"
                        hide-details
                      >
                        <template v-slot:label>
                          <div class="d-flex align-center">
                            <v-icon :color="vehicleType.color" class="mr-2">{{ vehicleType.icon }}</v-icon>
                            <span>{{ vehicleType.label }}</span>
                          </div>
                        </template>
                      </v-checkbox>
                    </v-col>
                  </v-row>
                  <v-alert 
                    v-if="fields.vehicle_types.length === 0" 
                    type="info" 
                    text 
                    dense 
                    class="mt-3 mb-0"
                  >
                    <small>El equipamiento no estará asignado a ningún tipo de vehículo hasta que selecciones al menos uno.</small>
                  </v-alert>
                  <v-chip-group v-else class="mt-3" column>
                    <v-chip 
                      v-for="selectedType in selectedVehicleTypesLabels" 
                      :key="selectedType.value"
                      :color="selectedType.color"
                      outlined
                      small
                    >
                      <v-icon left small>{{ selectedType.icon }}</v-icon>
                      {{ selectedType.label }}
                    </v-chip>
                  </v-chip-group>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn :disabled="loading" @click="cancelForm()" color="grey darken-1" text class="px-4">
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-btn :loading="loading" :disabled="saveIsDisabled" @click="saveForm()" color="primary" class="px-4" depressed>
          <v-icon left>mdi-content-save</v-icon>
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    created: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    visible: true,
    loading: false,

    // Tipos de vehículos disponibles
    availableVehicleTypes: [
      {
        value: 'gasolina_diesel_hibrido',
        label: 'Gasolina/Diesel/Híbrido',
        icon: 'mdi-gas-station',
        color: 'blue'
      },
      {
        value: 'hibrido_enchufable',
        label: 'Híbrido Enchufable',
        icon: 'mdi-car-electric',
        color: 'green'
      },
      {
        value: 'electrico',
        label: 'Eléctrico',
        icon: 'mdi-lightning-bolt',
        color: 'amber'
      }
    ],

    // campos del formulario
    fields: {
      name: null,
      vehicle_types: []
    },

    // validaciones
    validations: {
      isValid: true,
      name: [
        (v) => !!v || 'Debe proporcionar el nombre del equipamiento.',
        (v) => (v && v.length >= 3) || 'El nombre debe tener al menos 3 caracteres.',
        (v) => (v && v.length <= 100) || 'El nombre no puede exceder 100 caracteres.'
      ]
    },

    // errores
    errors: {
      name: [],
      vehicle_types: []
    }
  }),

  computed: {
    ...mapGetters(['CURRENT_USER']),
    isNewRecord () {
      return !this.value || !this.value.id || this.created
    },
    title () {
      return this.isNewRecord ? 'Nuevo Equipamiento' : 'Modificar Equipamiento'
    },
    saveIsDisabled () {
      return this.loading || !this.validations.isValid
    },
    selectedVehicleTypesLabels () {
      return this.availableVehicleTypes.filter(type => 
        this.fields.vehicle_types.includes(type.value)
      )
    }
  },

  async created () {
    // Cargar datos si estamos editando
    if (this.value && this.value.id) {
      this.loading = true
      try {
        const result = await this.$store.dispatch('VIEW_EQUIPAMIENTO', this.value.id)
        if (result) {
          this.fields.name = result.name || this.value.name
          // Extraer los tipos de vehículos asignados
          if (result.assigned_vehicle_types && Array.isArray(result.assigned_vehicle_types)) {
            this.fields.vehicle_types = result.assigned_vehicle_types.map(type => type.vehicle_type)
          } else if (this.value.assigned_vehicle_types && Array.isArray(this.value.assigned_vehicle_types)) {
            this.fields.vehicle_types = this.value.assigned_vehicle_types.map(type => type.vehicle_type)
          } else {
            this.fields.vehicle_types = []
          }
        } else {
          // Si no se pudo cargar desde el store, usar los datos del prop
          this.fields.name = this.value.name
          this.fields.vehicle_types = this.value.assigned_vehicle_types 
            ? this.value.assigned_vehicle_types.map(type => type.vehicle_type)
            : []
        }
      } catch (error) {
        console.error('Error al cargar equipamiento:', error)
        // Fallback a los datos del prop
        this.fields.name = this.value.name
        this.fields.vehicle_types = this.value.assigned_vehicle_types 
          ? this.value.assigned_vehicle_types.map(type => type.vehicle_type)
          : []
      } finally {
        this.loading = false
      }
    } else {
      // Nuevo equipamiento - campos vacíos
      this.fields.name = null
      this.fields.vehicle_types = []
    }
  },

  methods: {
    async saveForm () {
      this.$refs.equipamientoForm.validate()
      if (this.validations.isValid) {
        this.loading = true

        // Limpiar errores previos
        this.errors = {
          name: [],
          vehicle_types: []
        }

        try {
          let equipamiento = {
            name: this.fields.name,
            vehicle_types: this.fields.vehicle_types
          }

          // Si estamos editando, incluir el ID
          if (!this.isNewRecord) {
            equipamiento.id = this.value.id
          }
          
          const response = await this.$store.dispatch(
            this.isNewRecord ? 'ADD_EQUIPAMIENTO' : 'UPDATE_EQUIPAMIENTO',
            equipamiento
          )

          if (response.error) {
            if (response.status === 422) {
              // Manejar errores de validación del servidor
              if (Array.isArray(response.data)) {
                response.data.forEach(({ field, message }) => {
                  if (this.errors[field]) {
                    this.errors[field].push(message)
                  }
                })
              } else if (response.data.errors) {
                // Formato alternativo de errores
                Object.keys(response.data.errors).forEach(field => {
                  if (this.errors[field]) {
                    this.errors[field] = response.data.errors[field]
                  }
                })
              }
            } else {
              console.error('Error del servidor:', response.data)
              this.$toast.error('Ocurrió un error al intentar guardar el equipamiento')
            }
            this.loading = false
            return
          }

          // Emitir evento de éxito con los datos del equipamiento
          this.$emit('save', { insert: this.isNewRecord, value: response.data })
          this.visible = false
        } catch (error) {
          console.error('Error general:', error)
          this.$toast.error('Ocurrió un error inesperado')
        } finally {
          this.loading = false
        }
      }
    },

    cancelForm () {
      this.$emit('cancel')
    },

    // Limpiar errores cuando el usuario empiece a escribir
    clearFieldError (fieldName) {
      if (this.errors[fieldName]) {
        this.errors[fieldName] = []
      }
    }
  },

  watch: {
    'fields.name' () {
      this.clearFieldError('name')
    },
    'fields.vehicle_types' () {
      this.clearFieldError('vehicle_types')
    }
  }
}
</script>

<style scoped>
.equipamiento-form-card {
  border-radius: 8px;
  overflow: hidden;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  color: rgba(0, 0, 0, 0.87);
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.section-title .v-icon {
  margin-right: 8px;
}

/* Estilos para los checkboxes de tipos de vehículos */
.v-input--checkbox .v-label {
  font-size: 14px;
}

/* Chips de resumen */
.v-chip-group {
  margin-top: 0;
}

/* Card de tipos de vehículos */
.v-card--outlined {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

/* Alertas */
.v-alert--text {
  background-color: rgba(33, 150, 243, 0.05) !important;
}
</style>
